import i18n from '@/utils/i18n';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getMatchedCode,
  partialMatchFn,
  sortByCodeMatchPriority,
} from '@/utils/scanning';
import { getMinAndMaxQuantity } from '@/contexts/item-context-utils';
import { getVisibleQuantity } from '@/screens/new-order/hooks/use-quantity-reducer';
import {
  groupSortMultiMatchResults,
  selectLowestPack,
} from '@/utils/pack-sort';
import { ItemsContext_VariantsPackFieldsFragment } from '@/types';
import {
  LocalScanData,
  refreshEntriesFromCatalog,
  useLocalScanData,
} from '@/hooks/offline-mode/use-local-scan-data';
import {
  LocalScanLookupPath,
  LocalScanMatchStatus,
  ScannerType,
  ScanSound,
} from '@/components/scanner/scanner-types';
import { OnDeviceScanningSearchType } from '@domain/configuration/types';
import { PackUnit } from '@/enums';
import { SpanAttribute } from '@/utils/sentry/attributes';
import { startSpan } from '@/utils/sentry/platform';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useNewOrderActionsContext } from './new-order-actions-context';
import { useNewOrderQuantityStoreApi } from '@/stores/new-order-quantity-store';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOrderGuideItemsContext } from './order-guide-items-context';
import { usePackSortConfig } from '@/hooks/use-pack-sort-config';
import { usePlayScanSounds } from '@/hooks/use-play-scan-sounds';
import { useScannerSearch } from '@/hooks/use-scanner-search';
import { useVariantPackInfoContext } from './variant-pack-info-context';
import { VariantPackEntry } from '@/adapters/offline-mode';

export interface MinimalMultiUpdateQuantityMap {
  [variantPackUUID: string]: {
    entry: VariantPackEntry;
    quantity: number;
  };
}

// variant pack UUID -> the VP code (external_item_id or UPC) that was used to match it
export type MatchedCodeMap = Record<string, string>;

// MinimalScanResults displays order guide and catalog results in different sections, so need to differentiate them
export interface LocalScanSearchResults {
  catalogResults: VariantPackEntry[];
  matchedCodes: MatchedCodeMap;
  orderGuideResults: VariantPackEntry[];
}

export interface SearchLocalResult {
  lookupPath: LocalScanLookupPath;
  matchStatus: LocalScanMatchStatus;
}

export interface LocalScannerContextType {
  cartEntries: VariantPackEntry[] | null;
  cartMatchedCodes: MatchedCodeMap;
  clearResults: () => void;
  codeRef: React.RefObject<string | null>;
  errorText: string | null;
  isOffDeviceSearchInProgress: boolean;
  localScanData: LocalScanData;
  offDeviceSearchResults: ItemsContext_VariantsPackFieldsFragment[] | null;
  removeFromOrder: (variantPackUUID: string) => void;
  searchAndUpdateOrderFn: (
    code: string,
    scannerType: ScannerType,
  ) => Promise<void>;
  searchLocal: (code: string) => Promise<SearchLocalResult | null>;
  searchResults: LocalScanSearchResults | null;
  setMultiUpdates: React.Dispatch<
    React.SetStateAction<MinimalMultiUpdateQuantityMap | null>
  >;
  syncCartEntries: () => void;
  updateCartWithMultiUpdates: () => void;
}

interface Props {
  children?: React.ReactNode;
}

export const LocalScannerContext =
  createContext<LocalScannerContextType | null>(null);

export const LocalScannerContextProvider: React.FC<Props> = ({ children }) => {
  const isOfflineModeActive = useOfflineModeActive();
  const playScanSound = usePlayScanSounds();
  const quantityStore = useNewOrderQuantityStoreApi();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { getVariantPackInfo } = useVariantPackInfoContext();

  const onDeviceScanningConfig = useDynamicConfigValue(
    'on_device_scanning_config',
  );
  const defaultMatchingPackUnit =
    onDeviceScanningConfig?.default_matching_pack_unit;
  const autoSelectLowestUom = Boolean(
    onDeviceScanningConfig?.auto_select_lowest_uom,
  );
  const searchType =
    onDeviceScanningConfig?.search_type ??
    OnDeviceScanningSearchType.EXACT_MATCH;
  const packSortConfig = usePackSortConfig();

  const { allVariantPacks } = useOrderGuideItemsContext();
  const localScanData = useLocalScanData({
    autoDownload: true,
    loadEntries: true,
  });

  // losing connection causes useOrderGuideItemsContext to switch to offline mode data, even if it might be out of date
  // this will store the fresh OG data loaded on app startup
  const orderGuideUUIDsRef = useRef<Set<string> | null>(null);
  if (orderGuideUUIDsRef.current === null && allVariantPacks.length > 0) {
    orderGuideUUIDsRef.current = new Set(allVariantPacks.map((vp) => vp.uuid));
  }

  const {
    clearResults: clearScanResults,
    isOffDeviceSearchInProgress,
    offDeviceSearchResults,
    searchOffDevice,
  } = useScannerSearch();

  const codeRef = useRef<string>(null);

  const [capturedCartEntries, setCartEntries] = useState<
    VariantPackEntry[] | null
  >(null);
  const [capturedSearchResults, setSearchResults] =
    useState<LocalScanSearchResults | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [cartMatchedCodes, setCartMatchedCodes] = useState<MatchedCodeMap>({});
  const [multiUpdates, setMultiUpdates] =
    useState<MinimalMultiUpdateQuantityMap | null>(null);

  const { entriesByUUID } = localScanData;
  const cartEntries = useMemo(
    () =>
      capturedCartEntries &&
      refreshEntriesFromCatalog(capturedCartEntries, entriesByUUID),
    [capturedCartEntries, entriesByUUID],
  );
  const searchResults = useMemo(() => {
    if (!capturedSearchResults) {
      return capturedSearchResults;
    }

    const catalogResults = refreshEntriesFromCatalog(
      capturedSearchResults.catalogResults,
      entriesByUUID,
    );
    const orderGuideResults = refreshEntriesFromCatalog(
      capturedSearchResults.orderGuideResults,
      entriesByUUID,
    );

    return catalogResults === capturedSearchResults.catalogResults &&
      orderGuideResults === capturedSearchResults.orderGuideResults
      ? capturedSearchResults
      : {
          catalogResults,
          matchedCodes: capturedSearchResults.matchedCodes,
          orderGuideResults,
        };
  }, [capturedSearchResults, entriesByUUID]);

  const getActiveVPUUIDs = useCallback(() => {
    const { localQuantityMap, savedQuantityMap } =
      quantityStore.getState().quantityState;
    return Object.entries({
      ...savedQuantityMap,
      ...localQuantityMap,
    }).reduce<string[]>((acc, [uuid, quantity]) => {
      if (quantity > 0) {
        acc.push(uuid);
      }
      return acc;
    }, []);
  }, [quantityStore]);

  useEffect(() => {
    if (!localScanData.isLoaded || capturedCartEntries) {
      return;
    }

    const activeUUIDs = getActiveVPUUIDs();
    const entries: VariantPackEntry[] = [];
    for (const uuid of activeUUIDs) {
      const entry = localScanData.entriesByUUID.get(uuid);
      if (entry) {
        entries.push(entry);
      }
    }
    setCartEntries(entries);
  }, [
    localScanData.entriesByUUID,
    localScanData.isLoaded,
    capturedCartEntries,
    getActiveVPUUIDs,
  ]);

  const addToOrderFromEntry = useCallback(
    (entry: VariantPackEntry, matchedCode: string | null): boolean => {
      const quantity = getVisibleQuantity(
        quantityStore.getState().quantityState,
        entry.uuid,
      );
      const {
        order_max_quantity: maxQuantity,
        order_min_quantity: minQuantity,
      } = entry;

      let newQuantity = quantity + 1;
      let scanSound =
        newQuantity === 1
          ? ScanSound.IncrementFirst
          : ScanSound.IncrementSubsequent;

      if (typeof minQuantity === 'number' && newQuantity < minQuantity) {
        newQuantity = minQuantity;
      } else if (
        typeof maxQuantity === 'number' &&
        newQuantity >= maxQuantity
      ) {
        newQuantity = maxQuantity;
        scanSound = ScanSound.MaxQuantity;
      }

      playScanSound(scanSound);

      if (matchedCode) {
        setCartMatchedCodes((prev) =>
          prev[entry.uuid] === matchedCode
            ? prev
            : { ...prev, [entry.uuid]: matchedCode },
        );
      }

      setCartEntries((prev) => {
        if (!prev) {
          return [entry];
        }
        if (prev[0]?.uuid === entry.uuid) {
          return prev;
        }

        return prev.some((p) => p.uuid === entry.uuid)
          ? [entry, ...prev.filter((e) => e.uuid !== entry.uuid)]
          : [entry, ...prev];
      });

      debouncedUpdateQuantity(
        {
          eventPayloads: null,
          newQuantity,
          previousQuantity: quantity,
          variantPackDetails: {
            itemDisplayName: entry.display_name,
            metadata: null,
          },
          variantPackUUID: entry.uuid,
        },
        { source: 'scanner' },
      );
      return true;
    },
    [debouncedUpdateQuantity, playScanSound, quantityStore],
  );

  const offDeviceResultToEntry = useCallback(
    (
      variantPack: ItemsContext_VariantsPackFieldsFragment,
    ): VariantPackEntry => {
      // this function only gets called when online, so can use getMinAndMaxQuantity
      const { maximumQuantity, minimumQuantity } = getMinAndMaxQuantity(
        getVariantPackInfo(variantPack.uuid),
      );

      return {
        description: variantPack.item.description ?? null,
        display_name: variantPack.item.display_name,
        external_item_id: variantPack.external_item_id ?? null,
        item_uuid: variantPack.item.uuid,
        ...(maximumQuantity !== null
          ? { order_max_quantity: maximumQuantity }
          : {}),
        ...(minimumQuantity !== null
          ? { order_min_quantity: minimumQuantity }
          : {}),
        pack_max_unit_size: variantPack.pack?.max_unit_size ?? 1,
        pack_min_unit_size: variantPack.pack?.min_unit_size ?? 1,
        pack_unit: variantPack.pack?.unit ?? null,
        unit_count: variantPack.pack?.unit_count,
        upc_list: variantPack.upc_list ?? null,
        uuid: variantPack.uuid,
      };
    },
    [getVariantPackInfo],
  );

  const updateCartWithMultiUpdatesLocal = useCallback(() => {
    if (!multiUpdates) {
      return;
    }

    const { updatedEntries, uuidsToRemove } = Object.entries(
      multiUpdates,
    ).reduce<{
      updatedEntries: VariantPackEntry[];
      uuidsToRemove: Set<string>;
    }>(
      (acc, [variantPackUUID, { quantity, entry }]) => {
        if (quantity === 0) {
          acc.uuidsToRemove.add(variantPackUUID);
        } else {
          acc.updatedEntries.push(entry);
        }
        return acc;
      },
      { updatedEntries: [], uuidsToRemove: new Set() },
    );
    const updatedUUIDs = new Set(updatedEntries.map((e) => e.uuid));

    const searchMatchedCodes = capturedSearchResults?.matchedCodes;
    if (searchMatchedCodes) {
      setCartMatchedCodes((prev) => {
        const next = { ...prev };
        for (const uuid of updatedUUIDs) {
          if (searchMatchedCodes[uuid]) {
            next[uuid] = searchMatchedCodes[uuid];
          }
        }
        return next;
      });
    }

    setCartEntries((prev) => {
      if (!prev) {
        return null;
      }
      return [
        ...updatedEntries,
        ...prev.filter(
          (e) => !uuidsToRemove.has(e.uuid) && !updatedUUIDs.has(e.uuid),
        ),
      ];
    });
    setMultiUpdates(null);
  }, [capturedSearchResults, multiUpdates]);

  const updateCartWithMultiUpdates = useCallback(() => {
    clearScanResults();
    setSearchResults(null);
    updateCartWithMultiUpdatesLocal();
  }, [clearScanResults, updateCartWithMultiUpdatesLocal]);

  const buildMatchedCodes = (entries: VariantPackEntry[], code: string) =>
    entries.reduce<MatchedCodeMap>((acc, entry) => {
      const matchedCode = getMatchedCode(entry, code);
      if (matchedCode) {
        acc[entry.uuid] = matchedCode;
      }
      return acc;
    }, {});

  const searchLocal = useCallback(
    async (code: string) => {
      setErrorText(null);

      const addMatchedEntryToOrder = (entry: VariantPackEntry) =>
        addToOrderFromEntry(entry, getMatchedCode(entry, code));

      let matchedUUIDs: string[];
      const seenUUIDs = new Set();

      if (searchType === OnDeviceScanningSearchType.PARTIAL_MATCH) {
        matchedUUIDs = [];

        // partial mode makes us check each code in the index
        for (const [indexedCode, uuids] of localScanData.codeIndex) {
          if (partialMatchFn(code, [indexedCode])) {
            for (const uuid of uuids) {
              // upc_list codes can be substrings of each other like ['1234', '01234'], so need to check if VP is already added
              if (!seenUUIDs.has(uuid)) {
                matchedUUIDs.push(uuid);
                seenUUIDs.add(uuid);
              }
            }
          }
        }
      } else {
        matchedUUIDs = localScanData.codeIndex.get(code.toLowerCase()) ?? [];
      }

      if (matchedUUIDs.length > 0) {
        const orderGuideUUIDs = orderGuideUUIDsRef.current;

        // OG and catalog results are shown separately
        const orderGuideResults: VariantPackEntry[] = [];
        const catalogResults: VariantPackEntry[] = [];

        // # of tag and non-tag results determine auto-select logic below
        const nonTagOrderGuideResults: VariantPackEntry[] = [];
        const nonTagCatalogResults: VariantPackEntry[] = [];

        for (const uuid of matchedUUIDs) {
          const entry = localScanData.entriesByUUID.get(uuid);
          if (!entry) {
            continue;
          }
          const isTag = entry.pack_unit === PackUnit.Tag;
          if (orderGuideUUIDs?.has(uuid)) {
            orderGuideResults.push(entry);
            if (!isTag) {
              nonTagOrderGuideResults.push(entry);
            }
          } else {
            catalogResults.push(entry);
            if (!isTag) {
              nonTagCatalogResults.push(entry);
            }
          }
        }

        const nonTagTotal =
          nonTagOrderGuideResults.length + nonTagCatalogResults.length;

        // Case: auto-select if there is only 1 non-Tag result
        if (nonTagTotal === 1) {
          const entry = nonTagOrderGuideResults[0] ?? nonTagCatalogResults[0];
          addMatchedEntryToOrder(entry);
          return {
            lookupPath: LocalScanLookupPath.Local,
            matchStatus: LocalScanMatchStatus.Hit,
          };
        }

        // Case: auto-select if exactly 1 non-Tag VP is in an OG
        if (
          nonTagOrderGuideResults.length === 1 &&
          nonTagCatalogResults.length > 0
        ) {
          addMatchedEntryToOrder(nonTagOrderGuideResults[0]);
          return {
            lookupPath: LocalScanLookupPath.Local,
            matchStatus: LocalScanMatchStatus.Hit,
          };
        }

        // Case: auto-select if one matches default_matching_pack_unit
        if (nonTagTotal > 1 && defaultMatchingPackUnit) {
          const targetResults =
            nonTagOrderGuideResults.length > 1
              ? nonTagOrderGuideResults
              : nonTagCatalogResults;
          const packFiltered = targetResults.filter(
            (e) => e.pack_unit === defaultMatchingPackUnit,
          );
          if (packFiltered.length === 1) {
            addMatchedEntryToOrder(packFiltered[0]);
            return {
              lookupPath: LocalScanLookupPath.Local,
              matchStatus: LocalScanMatchStatus.Hit,
            };
          }
        }

        // Case: auto-select the lowest-UOM pack when still multi-matched
        if (nonTagTotal > 1 && autoSelectLowestUom) {
          const targetResults =
            nonTagOrderGuideResults.length > 1
              ? nonTagOrderGuideResults
              : nonTagCatalogResults;
          // targetResults can't be empty here: nonTagTotal > 1 guarantees
          // either nonTagOrderGuideResults or nonTagCatalogResults has at
          // least one entry. selectLowestPack only returns null for an empty
          // list, so this is just satisfying its generic T | null signature.
          const lowestEntry = selectLowestPack(targetResults, (e) => ({
            displayString: e.display_name,
            id: e.uuid,
            unit: e.pack_unit as PackUnit,
            unitCount: e.unit_count,
          }));
          if (lowestEntry) {
            addMatchedEntryToOrder(lowestEntry);
            return {
              lookupPath: LocalScanLookupPath.Local,
              matchStatus: LocalScanMatchStatus.Hit,
            };
          }
        }

        // Case: Can't auto-select, show all results including Tags
        const totalResults = orderGuideResults.length + catalogResults.length;
        if (totalResults > 1) {
          playScanSound(ScanSound.MultipleResults);
          // Ranks by code match priority, then groups by product (a
          // partial/broad code match can span multiple products) before
          // sorting each product's packs. Grouping keeps first-encountered
          // order, so each product lands at its best-ranked pack. See
          // groupSortMultiMatchResults for the auto_select_lowest_uom
          // behavior, shared with the on-device chooser in scan-results.
          const groupSortResults = (entries: VariantPackEntry[]) =>
            groupSortMultiMatchResults(
              sortByCodeMatchPriority(entries, code),
              (e) => e.item_uuid,
              (e) => ({
                displayString: e.display_name,
                id: e.uuid,
                unit: e.pack_unit as PackUnit,
                unitCount: e.unit_count,
              }),
              autoSelectLowestUom,
              packSortConfig,
            );
          setSearchResults({
            catalogResults: groupSortResults(catalogResults),
            matchedCodes: buildMatchedCodes(
              [...catalogResults, ...orderGuideResults],
              code,
            ),
            orderGuideResults: groupSortResults(orderGuideResults),
          });
          return {
            lookupPath: LocalScanLookupPath.Local,
            matchStatus: LocalScanMatchStatus.Ambiguous,
          };
        }

        // Case: auto-select if the single match is a tag
        if (totalResults === 1) {
          const entry = orderGuideResults[0] ?? catalogResults[0];
          addMatchedEntryToOrder(entry);
          return {
            lookupPath: LocalScanLookupPath.Local,
            matchStatus: LocalScanMatchStatus.Hit,
          };
        }
      }

      // Case: code not found and _online_
      if (codeRef.current === code && !isOfflineModeActive) {
        const offDeviceResults = await searchOffDevice(code);

        if (offDeviceResults.length === 1) {
          addMatchedEntryToOrder(offDeviceResultToEntry(offDeviceResults[0]));
          return {
            lookupPath: LocalScanLookupPath.Network,
            matchStatus: LocalScanMatchStatus.Hit,
          };
        } else if (offDeviceResults.length === 0) {
          playScanSound(ScanSound.NoResults);
          setErrorText(i18n.t('scan.no_results'));
          return {
            lookupPath: LocalScanLookupPath.Network,
            matchStatus: LocalScanMatchStatus.Miss,
          };
        }

        const offDeviceEntries = offDeviceResults.map(offDeviceResultToEntry);
        setSearchResults({
          catalogResults: offDeviceEntries,
          matchedCodes: buildMatchedCodes(offDeviceEntries, code),
          orderGuideResults: [],
        });
        return {
          lookupPath: LocalScanLookupPath.Network,
          matchStatus: LocalScanMatchStatus.Ambiguous,
        };
      }

      if (isOfflineModeActive) {
        // Case: code not found and _offline_
        playScanSound(ScanSound.NoResults);
        setErrorText(i18n.t('scan.no_results'));
        return {
          lookupPath: LocalScanLookupPath.Local,
          matchStatus: LocalScanMatchStatus.Miss,
        };
      }

      return null;
    },
    [
      searchType,
      isOfflineModeActive,
      localScanData.codeIndex,
      localScanData.entriesByUUID,
      defaultMatchingPackUnit,
      autoSelectLowestUom,
      packSortConfig,
      addToOrderFromEntry,
      offDeviceResultToEntry,
      playScanSound,
      searchOffDevice,
    ],
  );

  const searchAndUpdateOrderFn = useCallback(
    async (code: string, scannerType: ScannerType) => {
      codeRef.current = code;
      if (!code) {
        clearScanResults();
        setSearchResults(null);
        return;
      }

      updateCartWithMultiUpdates();

      await startSpan(
        {
          attributes: {
            [SpanAttribute.LocalScanEventScannerType]: scannerType,
            [SpanAttribute.LocalScanEventCode]: code,
          },
          forceTransaction: true,
          name: 'local_scan.event',
          op: 'local_scan.event',
        },
        async (span) => {
          const result = await searchLocal(code);
          if (result) {
            span.setAttributes({
              [SpanAttribute.LocalScanEventLookupPath]: result.lookupPath,
              [SpanAttribute.LocalScanEventMatchStatus]: result.matchStatus,
            });
          }
        },
      );
    },
    [clearScanResults, searchLocal, updateCartWithMultiUpdates],
  );

  const clearResults = useCallback(() => {
    clearScanResults();
    setSearchResults(null);
    setErrorText(null);
  }, [clearScanResults]);

  const removeFromOrder = useCallback((variantPackUUID: string) => {
    setCartEntries(
      (prev) => prev?.filter((e) => e.uuid !== variantPackUUID) ?? null,
    );
  }, []);

  const syncCartEntries = useCallback(() => {
    const activeUUIDs = getActiveVPUUIDs();
    setCartEntries((prev) => {
      if (prev) {
        return prev.filter((e) => activeUUIDs.includes(e.uuid));
      }
      return prev;
    });
  }, [getActiveVPUUIDs]);

  const contextValue = useMemo(
    () => ({
      cartEntries,
      cartMatchedCodes,
      clearResults,
      codeRef,
      errorText,
      isOffDeviceSearchInProgress,
      localScanData,
      offDeviceSearchResults,
      removeFromOrder,
      searchAndUpdateOrderFn,
      searchLocal,
      searchResults,
      setMultiUpdates,
      syncCartEntries,
      updateCartWithMultiUpdates,
    }),
    [
      cartEntries,
      cartMatchedCodes,
      clearResults,
      errorText,
      isOffDeviceSearchInProgress,
      offDeviceSearchResults,
      removeFromOrder,
      searchAndUpdateOrderFn,
      searchLocal,
      searchResults,
      syncCartEntries,
      localScanData,
      updateCartWithMultiUpdates,
      codeRef,
    ],
  );

  return (
    <LocalScannerContext.Provider value={contextValue}>
      {children}
    </LocalScannerContext.Provider>
  );
};

export const useLocalScannerContext = (): LocalScannerContextType => {
  const context = useContext(LocalScannerContext);
  if (!context) {
    throw new Error(
      'useLocalScannerContext must be used within a LocalScannerContextProvider',
    );
  }
  return context;
};

export const useMaybeLocalScannerContext =
  (): LocalScannerContextType | null => {
    return useContext(LocalScannerContext);
  };
